import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  List,
  ListItem,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Stack, CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatDate } from '@lib/utils';
import { useState } from 'react';
import {PropertyManifestEntry} from "@features/home-manifest/types";
import React from 'react';
import {ManifestDevice} from "@features/plan-manifest/types";

interface SmartstartPairingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  smartstartDevicesToPair: PropertyManifestEntry[];
}

export const SmartstartPairingDialog: React.FC<SmartstartPairingDialogProps> = ({
  isOpen,
  onClose,
  smartstartDevicesToPair
}) => {
  const [copyTooltip, setCopyTooltip] = useState<string>('Copy DSK');

  const handleClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick") {
      return;
    }
    onClose();
  };

  const handleCopyDSK = async (dsk: string) => {
    try {
      await navigator.clipboard.writeText(dsk);
      setCopyTooltip('Copied!');
      setTimeout(() => setCopyTooltip('Copy DSK'), 2000);
    } catch (err) {
      console.error('Failed to copy DSK:', err);
      setCopyTooltip('Failed to copy');
      setTimeout(() => setCopyTooltip('Copy DSK'), 2000);
    }
  };

  const hasPendingSensors = (sensorMap: Record<string, any>) => {
    if (!sensorMap) return false;
    return Object.values(sensorMap).some(value => value === "");
  };

  const renderSensorMap = (sensorMap: Record<string, any>) => {
    if (!sensorMap || Object.keys(sensorMap).length === 0) return 'None';

    return (
      <Stack spacing={0.5}>
        {Object.entries(sensorMap).map(([key, value]) => (
          <Box component={"div"} key={key} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {key}:
            </Typography>
            <Typography variant="body2" color={value === "" ? "warning.main" : "primary"}>
              {value === "" ? "Pending..." : value}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="smartstart-pairing-dialog-title"
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          height: '90vh', // Takes up 90% of the viewport height
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle id="smartstart-pairing-dialog-title">
        SmartStart Pairing
      </DialogTitle>
      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
        {smartstartDevicesToPair.length > 0 ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              SmartStart pairing process is in progress, please turn these SmartStart devices on, and do not close this dialog until all devices are paired
            </Alert>

            <Box component={"div"} sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              <List sx={{
                width: '100%',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}>
                {smartstartDevicesToPair.map((entry, index) => {
                  const device = entry.device as ManifestDevice;
                  const isPending = hasPendingSensors(entry.sensor_map || {});
                  const allMapped = entry.sensor_map && Object.values(entry.sensor_map).every(v => v !== "");
                  const isProvisioned = Boolean(entry.provisioned);
                  const showCheck = allMapped && isProvisioned;

                  return (
                    <React.Fragment key={entry.manifest_entry_id}>
                      {index > 0 && <Divider />}
                      <ListItem
                        sx={{
                          py: 1.5,
                          display: 'grid',
                          gridTemplateColumns: '1.2fr 80px 1.5fr auto 48px', // Adjusted to reduce device name width and increase sensor map
                          gap: 2,
                          alignItems: 'center',
                          position: 'relative',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <Box component={"div"}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                            {showCheck && <CheckCircleIcon color="success" sx={{ mr: 1 }} />}
                            {device.friendly_name || device.description || 'Unnamed Device'}
                          </Typography>
                        </Box>

                        <Box component={"div"} sx={{ display: 'flex', alignItems: 'center' }}>
                          {entry.dsk ? (
                            <Tooltip title={copyTooltip}>
                              <IconButton
                                onClick={() => handleCopyDSK(entry.dsk || '')}
                                size="small"
                                aria-label="copy DSK"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2">N/A</Typography>
                          )}
                        </Box>

                        <Box component={"div"} sx={{ minWidth: 0 }}>
                          {renderSensorMap(entry.sensor_map || {})}
                        </Box>

                        <Box component={"div"} sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {entry.provisioned ? formatDate(entry.provisioned).fromNow() : 'Not provisioned'}
                          </Typography>
                        </Box>

                        <Box component={"div"} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {isPending && (
                            <Tooltip title="Waiting for sensor mapping">
                              <CircularProgress size={24} />
                            </Tooltip>
                          )}
                        </Box>
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          </>
          ) : (
          <Alert severity="success" sx={{ mb: 2 }}>
            All SmartStart devices have been successfully paired!
          </Alert>
          )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

